import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PageHero, Section, SectionHeading, Seo, Reveal, breadcrumb } from "@/components/site/Section";
import { PlansGrid } from "@/components/site/PlanCard";
import { CustomCodeBox } from "@/components/site/CustomCodeBox";
import { FinalCTA } from "@/components/site/HomeSections";
import { FAQList } from "@/components/site/FAQList";
import { usePlans, formatGBP } from "@/lib/api";
import { FAQS } from "@/data/faqs";

const ROWS = [
  ["Bags of kiln-dried logs", (p) => p.items.log_bags],
  ["Bags of kindling", (p) => p.items.kindling_bags],
  ["Packs of 10 firelighters", (p) => p.items.firelighter_packs],
  ["Delivery", () => "Monthly"],
  ["Local delivery", () => "Included"],
  ["Monthly price", (p) => formatGBP(p.price_pence)],
];

const Compare = () => {
  const { data: plans } = usePlans();
  if (!plans) return null;
  return (
    <div className="mt-12 overflow-x-auto rounded-3xl border border-[#262626]" data-testid="plans-compare-table">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="bg-[#111]">
          <tr>
            <th className="p-5 font-medium text-[#A1A1AA]" scope="col">What's included</th>
            {plans.map((p) => <th key={p.slug} scope="col" className="p-5 font-display text-lg font-bold">{p.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, fn]) => (
            <tr key={label} className="border-t border-[#1f1f1f]">
              <th scope="row" className="p-5 font-normal text-[#F7F5F0]/80">{label}</th>
              {plans.map((p) => <td key={p.slug} className="p-5 font-semibold">{fn(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const INCLUDED = ["Kiln-dried logs, ready to burn", "Kindling and firelighters", "Local delivery in eligible postcodes", "Pause for 1–3 months", "Skip a delivery", "Change plan or cancel online"];

const SITE = process.env.REACT_APP_CANONICAL_URL;

const productLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: `WeeLogRun ${p.name} firewood subscription`,
  description: `${p.description} Includes ${p.contents.join(", ")}.`,
  brand: { "@type": "Brand", name: "WeeLogRun" },
  image: `${SITE}/img/logs-stack.jpg`,
  offers: {
    "@type": "Offer",
    price: (p.price_pence / 100).toFixed(2),
    priceCurrency: "GBP",
    availability: "https://schema.org/InStock",
    url: `${SITE}/checkout?plan=${p.slug}`,
    areaServed: "Newtownabbey and surrounding areas (BT36–BT39)",
    priceSpecification: { "@type": "UnitPriceSpecification", price: (p.price_pence / 100).toFixed(2), priceCurrency: "GBP", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } },
  },
});

export default function Plans() {
  const { data: plans } = usePlans();
  return (
    <>
      <Seo title="Firewood Subscription Plans & Prices" path="/plans" description="Compare WeeLogRun firewood subscription plans: Starter, Regular and Family, or ask for a custom plan. Kiln-dried logs, kindling and firelighters delivered monthly in Newtownabbey and surrounding areas."
        jsonLd={[breadcrumb("Our Plans", "/plans"), ...(plans || []).map(productLd)]} />
      <PageHero eyebrow="Our plans" title="Simple plans. Warmer homes." intro="Three monthly plans, each with kiln-dried logs, kindling and firelighters delivered to your door. No contracts to sign, no hassle. Need something different? We now build custom plans too.">
        <div className="mt-8 flex max-w-2xl flex-col gap-4 rounded-2xl border border-[#D4A437]/40 bg-[#D4A437]/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="custom-plans-callout">
          <div>
            <p className="font-semibold text-[#F3E0A2]">New: custom plans</p>
            <p className="mt-1 text-sm text-[#D8D6D0]">Tell us what you need and how often. We'll agree a monthly price and give you a personal code to sign up. Ask about adding our smokeless coal (20kg bags, £12 each) to your custom plan too.</p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Link to="/contact" className="lc-btn-primary whitespace-nowrap !py-2.5" data-testid="custom-plans-contact">Ask for a custom plan</Link>
            <a href="#custom-code" className="lc-btn-ghost whitespace-nowrap !py-2.5" data-testid="custom-plans-have-code">I have a code</a>
          </div>
        </div>
      </PageHero>
      <Section className="!pt-16"><PlansGrid /><CustomCodeBox /></Section>
      <Section className="bg-[#0b0b0b]">
        <SectionHeading eyebrow="Compare" title="Plans side by side." />
        <Compare />
        <Reveal delay={0.1} className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((t) => (
            <p key={t} className="flex items-center gap-3 rounded-2xl border border-[#262626] bg-[#111] p-4 text-[15px]">
              <Check className="h-5 w-5 text-[#FF6A00]" /> {t}
            </p>
          ))}
        </Reveal>
      </Section>
      <Section>
        <SectionHeading eyebrow="Questions" title="About our plans." />
        <div className="mt-10"><FAQList items={FAQS.filter((f) => f.cat === "Plans" || f.cat === "Your subscription")} prefix="plans-faq" /></div>
      </Section>
      <FinalCTA />
    </>
  );
}
